import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ADMIN_PANEL_ROLES } from "@/lib/adminAccess";
import { isNativeShell } from "@/pos/lib/platform";
import { isStaffSurface } from "@/lib/staffSurfaces";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import SiteHeader from "./components/SiteHeader";
import { LoadingBusLottie } from "@/components/ui/loading-bus-lottie";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Critical routes — eager loaded (homepage + search = 80% of traffic)
import Index from "./pages/Index.tsx";
import SearchResults from "./pages/SearchResults.tsx";

// Auth routes — lazy loaded (only needed on /signin, /signup)
const Login = lazy(() => import("./pages/Login.tsx"));
const Register = lazy(() => import("./pages/Register.tsx"));

// Non-critical routes — lazy loaded
const SeatSelection = lazy(() => import("./pages/SeatSelection.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const TicketConfirmation = lazy(() => import("./pages/TicketConfirmation.tsx"));
const ManageBooking = lazy(() => import("./pages/ManageBooking.tsx"));
const LiveTracking = lazy(() => import("./pages/LiveTracking.tsx"));
const RoutesFleet = lazy(() => import("./pages/RoutesFleet.tsx"));
const Counters = lazy(() => import("./pages/Counters.tsx"));
const Support = lazy(() => import("./pages/Support.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const AdminProfile = lazy(() => import("./pages/AdminProfile.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const PassengerDashboard = lazy(() => import("./pages/PassengerDashboard.tsx"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess.tsx"));
const PaymentFail = lazy(() => import("./pages/PaymentFail.tsx"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const TicketShare = lazy(() => import("./pages/TicketShare"));
const MyComplaints = lazy(() => import("./pages/MyComplaints.tsx"));
const MyTicketDetails = lazy(() => import("./pages/MyTicketDetails.tsx"));
const Notices = lazy(() => import("./pages/Notices.tsx"));
const NoticeDetail = lazy(() => import("./pages/NoticeDetail.tsx"));
const StaffLogin = lazy(() => import("./pages/staff/StaffLogin.tsx"));
const StaffTrips = lazy(() => import("./pages/staff/StaffTrips.tsx"));
const TripControl = lazy(() => import("./pages/staff/TripControl.tsx"));
const BusStaffDashboard = lazy(() => import("./pages/staff/BusStaffDashboard.tsx"));
const SupervisorDashboard = lazy(() => import("./pages/staff/SupervisorDashboard.tsx"));
const CounterLogin = lazy(() => import("./pages/counter/CounterLogin.tsx"));
const CounterAdmin = lazy(() => import("./pages/counter/CounterAdmin.tsx"));
// Counter SmartPOS — the surface the Capacitor Android shell wraps.
const CounterPos = lazy(() => import("./pages/pos/CounterPos.tsx"));
const PosTicket = lazy(() => import("./pages/pos/PosTicket.tsx"));
const CounterProfile = lazy(() => import("./pages/counter/CounterProfile.tsx"));
const FinanceLogin = lazy(() => import("./pages/finance/FinanceLogin.tsx"));
const FinanceDashboard = lazy(() => import("./pages/finance/FinanceDashboard.tsx"));
const FinanceProfile = lazy(() => import("./pages/finance/FinanceProfile.tsx"));
const SupportStaffLogin = lazy(() => import("./pages/support-staff/SupportLogin.tsx"));
const SupportStaffDashboard = lazy(() => import("./pages/support-staff/SupportDashboard.tsx"));
const SupportStaffProfile = lazy(() => import("./pages/support-staff/SupportProfile.tsx"));
const HrLogin = lazy(() => import("./pages/hr/HrLogin.tsx"));
const HrDashboard = lazy(() => import("./pages/hr/HrDashboard.tsx"));
const HrProfile = lazy(() => import("./pages/hr/HrProfile.tsx"));
const HrMe = lazy(() => import("./pages/hr/HrMe.tsx"));
const VerifyStaffCard = lazy(() => import("./pages/VerifyStaffCard.tsx"));
const AIChatWidget = lazy(() => import("./components/support/AIChatWidget"));
const Maintenance = lazy(() => import("./pages/Maintenance"));
const Health = lazy(() => import("./pages/Health"));

// Driver PWA — lazy loaded
const DriverLayout = lazy(() => import("./components/driver/DriverLayout").then(m => ({ default: m.DriverLayout })));
const DriverHome = lazy(() => import("./pages/driver/DriverHome"));
const DriverActiveTrip = lazy(() => import("./pages/driver/ActiveTrip"));
const DriverRouteProgress = lazy(() => import("./pages/driver/RouteProgress"));
const DriverSyncStatus = lazy(() => import("./pages/driver/SyncStatus"));
const DriverProfile = lazy(() => import("./pages/driver/DriverProfile"));

// Dev-only demo page
const LoadersDemo = import.meta.env.DEV ? lazy(() => import("./pages/LoadersDemo")) : null;

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: { retry: false },
    queries: { staleTime: 30_000, retry: 1 },
  },
});

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <LoadingBusLottie size="lg" label="Loading Star Line..." />
  </div>
);

/**
 * The passenger support chatbot, on passenger surfaces only.
 *
 * It renders outside `<Routes>` so it survives navigation, which also meant it
 * rendered on every staff shell — a customer help bubble floating over the
 * counter admin's terminal desk and the POS, offering a clerk the passenger's
 * support channel. Same route test as the site header, from one shared list.
 */
const PassengerChat = () => {
  const { pathname } = useLocation();
  if (isStaffSurface(pathname)) return null;

  // Kept in its own boundary: a widget crash must never take down the app.
  return (
    <ErrorBoundary silent>
      <Suspense fallback={null}>
        <AIChatWidget />
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * Reset scroll position on navigation.
 *
 * React Router keeps the window's scroll offset across route changes, so
 * following "Select Seats" from halfway down a long search-results page opened
 * checkout already scrolled past its own heading — it read as a broken page.
 *
 * Hash links (`/terms#refunds`) and browser back/forward are left alone: POP
 * means the user is returning to somewhere they had already scrolled to, and
 * clobbering that is its own bug.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (hash || navigationType === 'POP') return;
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [pathname, hash, navigationType]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <LanguageProvider>
        <AuthProvider>
          <ErrorBoundary>
            <ScrollToTop />
            <SiteHeader />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/*
                  Public routes — eager.

                  The Android shell loads `https://localhost/`, so without this
                  the Counter POS opened on the **public passenger homepage** —
                  nav, chatbot and all — which §3.2a forbids the POS from ever
                  rendering. Capacitor has no "start route" setting for a local
                  bundle, and the two builds are the same bundle, so the shell
                  has to redirect itself. Guarded on `isNativeShell()`: in a desk
                  browser this is false and `/` still renders the homepage
                  untouched.
                */}
                <Route
                  path="/"
                  element={isNativeShell() ? <Navigate to="/pos" replace /> : <Index />}
                />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/signin" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Register />} />
                <Route path="/register" element={<Register />} />

                {/* Public routes — lazy */}
                <Route path="/seat-selection" element={<SeatSelection />} />
                <Route path="/routes" element={<RoutesFleet />} />
                <Route path="/counters" element={<Counters />} />
                <Route path="/support" element={<Support />} />
                <Route path="/live-tracking" element={<LiveTracking />} />
                <Route path="/notices" element={<Notices />} />
                <Route path="/notices/:id" element={<NoticeDetail />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/maintenance" element={<Maintenance />} />
                <Route path="/health" element={<Health />} />

                {/* Public ticket share — no auth, anyone with the link */}
                <Route path="/t/:bookingId" element={<TicketShare />} />

                {/* Payment return pages */}
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/fail" element={<PaymentFail />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />

                {/* Protected routes — require login */}
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/ticket" element={<ProtectedRoute><TicketConfirmation /></ProtectedRoute>} />
                <Route path="/manage-booking" element={<ManageBooking />} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><PassengerDashboard /></ProtectedRoute>} />
                <Route path="/my-complaints" element={<ProtectedRoute><MyComplaints /></ProtectedRoute>} />
            <Route path="/my-bookings/:bookingId" element={<ProtectedRoute><MyTicketDetails /></ProtectedRoute>} />

                {/* Admin control panel — graded multi-role access. Each role
                    sees only its permitted tabs (see lib/adminAccess); server
                    RLS/RPC guards enforce what each may actually do. */}
                <Route path="/admin" element={<ProtectedRoute allowRoles={ADMIN_PANEL_ROLES}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/profile" element={<ProtectedRoute requireAdmin><AdminProfile /></ProtectedRoute>} />

                {/* Staff routes — standalone pages (no SiteHeader) */}
                <Route path="/staff/login" element={<StaffLogin />} />
                <Route path="/staff/trips" element={<ProtectedRoute requireStaff><StaffTrips /></ProtectedRoute>} />
                <Route path="/staff/trip/:tripId" element={<ProtectedRoute requireStaff><TripControl /></ProtectedRoute>} />
                <Route path="/staff/onboard" element={<ProtectedRoute requireBusStaff><BusStaffDashboard /></ProtectedRoute>} />
                <Route path="/staff/supervisor" element={<ProtectedRoute requireSupervisor><SupervisorDashboard /></ProtectedRoute>} />

                {/* Counter staff routes */}
                <Route path="/counter/login" element={<CounterLogin />} />
                {/* The counter admin's terminal desk. The route gate is coarse
                    — *which* terminals they may see or change is enforced
                    server-side by counter_admin_may_manage() on every RPC. */}
                <Route path="/counter" element={<ProtectedRoute allowRoles={['counter_admin', 'admin']}><CounterAdmin /></ProtectedRoute>} />
                {/* `/counter/dashboard` is gone (2026-07-31). It was the legacy
                    clerk surface, superseded by the SmartPOS at `/pos` — two
                    competing sell paths for one role. `staffLandingPath` had
                    already stopped sending clerks there. */}
                <Route path="/counter/profile" element={<ProtectedRoute requireCounterStaff><CounterProfile /></ProtectedRoute>} />
                {/*
                  Deliberately not behind ProtectedRoute: the POS carries its
                  own sign-in and its own wrong-role screen, so a clerk never
                  lands on the passenger site's /login from the counter app.
                  The role gate still exists — it is inside CounterPos — and
                  every counter action is enforced server-side regardless.
                */}
                <Route path="/pos" element={<CounterPos />} />
                {/* Counter print surface — a bare thermal document, never the
                    customer ticket shell (§3.2a). Auth is enforced by the
                    audited lookup RPC it reads through. */}
                <Route path="/pos/print/:bookingId" element={<PosTicket />} />

                {/* Finance routes */}
                <Route path="/finance/login" element={<FinanceLogin />} />
                <Route path="/finance/dashboard" element={<ProtectedRoute requireFinance><FinanceDashboard /></ProtectedRoute>} />
                <Route path="/finance/profile" element={<ProtectedRoute requireFinance><FinanceProfile /></ProtectedRoute>} />

                {/* Support staff routes */}
                <Route path="/support-staff/login" element={<SupportStaffLogin />} />
                <Route path="/support-staff/dashboard" element={<ProtectedRoute requireSupport><SupportStaffDashboard /></ProtectedRoute>} />
                <Route path="/support-staff/profile" element={<ProtectedRoute requireSupport><SupportStaffProfile /></ProtectedRoute>} />

                {/* HR routes */}
                <Route path="/hr/login" element={<HrLogin />} />
                <Route path="/hr" element={<ProtectedRoute allowRoles={['hr', 'admin']}><HrDashboard /></ProtectedRoute>} />
                <Route path="/hr/profile" element={<ProtectedRoute requireHr><HrProfile /></ProtectedRoute>} />
                {/* Self-service: any member of staff, reading only their own record (the server scopes it). */}
                <Route
                  path="/hr/me"
                  element={(
                    <ProtectedRoute allowRoles={['hr', 'admin', 'finance', 'support', 'counter_staff', 'counter_admin', 'supervisor', 'staff', 'driver']}>
                      <HrMe />
                    </ProtectedRoute>
                  )}
                />
                {/* The QR code on a staff identity card — deliberately public. */}
                <Route path="/verify/staff/:token" element={<VerifyStaffCard />} />

                {/* Driver PWA routes — nested under DriverLayout */}
                <Route path="/driver" element={<ProtectedRoute requireStaff><DriverLayout /></ProtectedRoute>}>
                  <Route index element={<DriverHome />} />
                  <Route path="trip/:tripId" element={<DriverActiveTrip />} />
                  <Route path="route" element={<DriverRouteProgress />} />
                  <Route path="sync" element={<DriverSyncStatus />} />
                  <Route path="profile" element={<DriverProfile />} />
                </Route>

                {/* Dev-only demo route */}
                {import.meta.env.DEV && LoadersDemo && (
                  <Route path="/loaders-demo" element={<Suspense fallback={<LoadingFallback />}><LoadersDemo /></Suspense>} />
                )}

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <PassengerChat />
          </ErrorBoundary>
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
      <Analytics />
      <SpeedInsights />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
